// Story 10.2 — Reducer puro para los deltas en tiempo real de un Bot Card.
// Aislado del hook React para que los tests no necesiten React/RTL.

export type BotStatus = 'inactive' | 'active' | 'error' | 'deploying';

export interface RealtimeMetricsState {
  /** Conversaciones únicas (distinct `from`) en la sesión. */
  conversations: number;
  /** Mensajes recibidos en la sesión. */
  received: number;
  /** Mensajes enviados en la sesión. */
  sent: number;
  /** Override transitorio del status — null = usar el del backend. */
  statusOverride: BotStatus | null;
  /** True por X ms tras un `flow:error` para resaltar la card. */
  errorPulse: boolean;
  /** Set de `from` ya vistos — para no duplicar `conversations`.
   *  P-232: Set + cap para que sesiones largas no degraden perf en O(n)
   *  ni bloaten memoria. Cuando se llega al cap, el oldest entry se
   *  desaloja (FIFO via inserción ordenada de Set). */
  seenFromKeys: ReadonlySet<string>;
}

/** P-232: techo de senders únicos rastreados antes de empezar a desalojar.
 *  Un bot real con ~5k usuarios distintos por día es ya un caso enorme; el
 *  cap previene growth ilimitado en sesiones que el user deja abierta días. */
export const SEEN_FROM_CAP = 5000;

export type RealtimeEvent =
  | { type: 'reset' }
  | { type: 'message-received'; from: string }
  | { type: 'message-sent' }
  | { type: 'bot-started' }
  | { type: 'bot-stopped' }
  | { type: 'flow-error' }
  | { type: 'error-pulse-clear' };

export const INITIAL_REALTIME_STATE: RealtimeMetricsState = {
  conversations: 0,
  received: 0,
  sent: 0,
  statusOverride: null,
  errorPulse: false,
  seenFromKeys: new Set<string>(),
};

export function realtimeReducer(
  state: RealtimeMetricsState,
  action: RealtimeEvent,
): RealtimeMetricsState {
  switch (action.type) {
    case 'reset':
      return INITIAL_REALTIME_STATE;
    case 'message-received': {
      const isNewConversation = !state.seenFromKeys.has(action.from);
      let nextSeen = state.seenFromKeys;
      if (isNewConversation) {
        // FIFO eviction cuando se llega al cap. `Set` mantiene el orden de
        // inserción, así que `next.values().next().value` es el oldest.
        const next = new Set(state.seenFromKeys);
        if (next.size >= SEEN_FROM_CAP) {
          const oldest = next.values().next().value;
          if (oldest != null) next.delete(oldest);
        }
        next.add(action.from);
        nextSeen = next;
      }
      return {
        ...state,
        received: state.received + 1,
        conversations: isNewConversation ? state.conversations + 1 : state.conversations,
        seenFromKeys: nextSeen,
      };
    }
    case 'message-sent':
      return { ...state, sent: state.sent + 1 };
    case 'bot-started':
      return { ...state, statusOverride: 'active' };
    case 'bot-stopped':
      return { ...state, statusOverride: 'inactive' };
    case 'flow-error':
      return { ...state, errorPulse: true, statusOverride: 'error' };
    case 'error-pulse-clear':
      return { ...state, errorPulse: false };
  }
}
